import { createContext, useCallback, useContext, useEffect, useState } from "react";
import api, { SESSION_EXPIRED_EVENT, DEFAULT_EXPIRED_MSG } from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = loading, false = not authed
  const [ready, setReady] = useState(false);
  const [sessionMessage, setSessionMessage] = useState("");

  const clearSession = useCallback((message) => {
    localStorage.removeItem("zx_token");
    setUser(false);
    if (message) setSessionMessage(message);
  }, []);

  const startSession = (token, u) => {
    localStorage.setItem("zx_token", token);
    setSessionMessage("");
    setUser(u);
  };

  useEffect(() => {
    const token = localStorage.getItem("zx_token");
    if (!token) {
      setUser(false);
      setReady(true);
      return;
    }
    api
      .get("/me")
      .then((res) => setUser(res.data))
      .catch((err) => {
        const d = err.response?.data?.detail;
        clearSession(err.response?.status === 401 ? (typeof d === "string" ? d : DEFAULT_EXPIRED_MSG) : "");
      })
      .finally(() => setReady(true));
  }, [clearSession]);

  useEffect(() => {
    const onExpired = (e) => clearSession(e.detail || DEFAULT_EXPIRED_MSG);
    window.addEventListener(SESSION_EXPIRED_EVENT, onExpired);
    return () => window.removeEventListener(SESSION_EXPIRED_EVENT, onExpired);
  }, [clearSession]);

  // Returns { twofa: true } when a 2FA OTP step is required (admin accounts).
  const login = async (email, password) => {
    const res = await api.post("/auth/login", { email, password });
    if (res.data.twofa_required) {
      return { twofa: true };
    }
    startSession(res.data.access_token, res.data.user);
    return { twofa: false, user: res.data.user };
  };

  const verifyOtp = async (email, code) => {
    const res = await api.post("/auth/verify-otp", { email, code });
    startSession(res.data.access_token, res.data.user);
    return res.data.user;
  };

  const resendOtp = async (email) => {
    await api.post("/auth/resend-otp", { email });
  };

  const logout = async () => {
    try {
      await api.post("/auth/logout");
    } catch {
      // token may already be invalid
    }
    clearSession("");
  };

  return (
    <AuthContext.Provider value={{ user, ready, sessionMessage, clearSessionMessage: () => setSessionMessage(""), login, verifyOtp, resendOtp, logout }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
